<template>
  <v-card
    id="account-profile-billing"
    class="pl-8 pr-8 pt-4 pb-6 mt-4 mb-5 elevation-0"
    :class="{ 'pr-10': !isMobile }"
  >
    <BarLoading v-if="loading" />
    <div v-else class="mt-5">
      <h1 class="card-title">
        <span class="mr-3">{{ $t('views.myAccount.billing.currentPlan') }}</span>
      </h1>

      <div class="current-plan mt-2" :class="{ 'small-font': isMobile }">
        <v-icon
          v-if="hasPlan || user.status === 'TRIAL'"
          :size="isMobile ? 18 : 22"
          class="mb-1 mr-1"
        >
          {{ getPlanIcon() }}
        </v-icon>
        <template v-if="user.status === 'TRIAL'">
          {{ $t(`views.myAccount.billing.status.${user.status}`) }}
          ({{ $t('views.myAccount.billing.trialLeft1') }}{{ user.trialDays }}
          {{ $t('views.myAccount.billing.trialLeft2') }})
        </template>
        <span v-else>
          {{ $t(`views.myAccount.billing.status.${user.status}`) }}
        </span>
      </div>

      <div v-if="limits && Object.keys(limits).length" class="billing-limits mt-6">
        <h1 class="card-title">
          {{ $t(`views.myAccount.billing.limits`) }}
        </h1>

        <div class="items-flex mt-3">
          <currency
            :value="limits.totalCostWithComissionBaseCurrency"
            :currency="user.baseCurrency"
            :decimals="2"
          />
          <span class="mx-1">/</span>
          <currency
            v-if="limits.maxTotalCostWithComissionBaseCurrency"
            :value="limits.maxTotalCostWithComissionBaseCurrency"
            :currency="user.baseCurrency"
            :decimals="2"
          />
          <span v-else class="mr-1">∞</span>

          <div>
            <span class="ml-1">
              {{ $t(`views.myAccount.billing.maxTotalCost1`) }}
            </span>
            <span v-if="!isMobile" class="ml-0">
              {{ $t(`views.myAccount.billing.maxTotalCost2`) }}
            </span>
          </div>
        </div>
        <div class="billing-limits-progress-bar mt-1">
          <v-progress-linear
            :value="
              limits.totalCostWithComissionBaseCurrencyPercent === Infinity
                ? 0
                : limits.totalCostWithComissionBaseCurrencyPercent
            "
            :color="getProgressLinearColor(limits.totalCostWithComissionBaseCurrencyPercent)"
          ></v-progress-linear>
        </div>

        <div class="items-flex mt-5">
          <currency :value="limits.totalOperations" :decimals="2" />
          <span class="mx-1">/</span>
          <currency :value="limits.maxTotalOperations" :decimals="2" />
          <span class="ml-1">{{ $t(`views.myAccount.billing.maxOperations`) }}</span>
        </div>
        <div class="billing-limits-progress-bar mt-1">
          <v-progress-linear
            :value="limits.totalOperationsPercent"
            :color="getProgressLinearColor(limits.totalOperationsPercent)"
          ></v-progress-linear>
        </div>
      </div>

      <div class="billing-options mt-10">
        <!-- NO PLAN OR TRIAL-->
        <template v-if="!hasPlan">
          <!-- INCOMPLETE SUBSCRIPTION -->
          <div
            v-if="
              userAccountStripeSubscription &&
              ['incomplete'].indexOf(userAccountStripeSubscription.status) > -1
            "
            class="mb-3"
          >
            <v-alert color="softWarning" icon="mdi-alert">
              {{ $t('views.myAccount.billing.paymentIncomplete') }}
            </v-alert>
            <p class="mb-2">
              <a @click="completeIncompleteSubscription">
                {{ $t(`views.myAccount.billing.completeIncompleteSubscription`) }}
              </a>
            </p>
          </div>
          <div v-else>
            <v-btn
              color="accentYellow"
              class="upgrade-button elevation-0"
              @click="upgradeToPremium"
            >
              <v-icon size="16" class="mr-3" color="black">mdi-rocket-launch</v-icon>
              <span class="black--text">
                {{ $t('views.myAccount.billing.goPlan') }}
              </span>
            </v-btn>
          </div>
        </template>

        <!-- WITH PLAN MASTER OR STARTER -->
        <template v-else>
          <div class="billing-period-end">
            <!-- NOT CANCEL AT PERIOD END -->
            <div v-if="!userAccountStripeSubscription.cancelAtPeriodEnd">
              <!-- ACTIVE SUBSCRIPTION -->
              <div v-if="userAccountStripeSubscription.status === 'active'" class="mb-3">
                {{ $t(`views.myAccount.billing.yourPlanWillBeRenewed`) }}
                <span v-if="userAccountStripeSubscription.currentPeriodEnd">
                  {{ $t(`views.myAccount.billing.atDay`) }}
                  {{
                    getDateFromTimestampUserFormat(userAccountStripeSubscription.currentPeriodEnd)
                  }}.
                </span>
              </div>
              <!-- TRIALING SUBSCRIPTION -->
              <div v-if="userAccountStripeSubscription.status === 'trialing'" class="mb-3">
                {{ $t(`views.myAccount.billing.trialEnd`) }}
                <span v-if="userAccountStripeSubscription.currentPeriodEnd">
                  {{ $t(`views.myAccount.billing.atDay`) }}
                  {{
                    getDateFromTimestampUserFormat(userAccountStripeSubscription.currentPeriodEnd)
                  }}
                  {{ $t(`views.myAccount.billing.andWillBeRenewed`) }}
                  <br />
                  {{ $t(`views.myAccount.billing.notChargeIfCancel`) }}
                </span>
              </div>
              <!-- PAST_DUE SUBSCRIPTION -->
              <AccountPastDueAlert />

              <p class="mt-4 mb-2">
                <a @click="updateSubscription">
                  <span>
                    {{ $t(`views.myAccount.billing.updateSubscription`) }}
                  </span>
                </a>
              </p>
              <!-- <p class="mb-2">
                <a @click="updateSubscription">
                  <span v-if="userAccountStripeSubscription.priceRecurringInterval === 'year'">
                    {{ $t(`views.myAccount.billing.updateSubscriptionToMonthly`) }}
                  </span>
                  <span
                    v-else-if="userAccountStripeSubscription.priceRecurringInterval === 'month'"
                  >
                    {{ $t(`views.myAccount.billing.updateSubscriptionToYearly`) }}
                  </span>
                  <span v-else>
                    {{ $t(`views.myAccount.billing.updateSubscription`) }}
                  </span>
                </a>
              </p> -->
              <p class="mb-2">
                <a @click="changePaymentMethod">
                  {{ $t(`views.myAccount.billing.changePaymentMethod`) }}
                </a>
              </p>
              <p>
                <a @click="cancelSubscription">
                  {{ $t(`views.myAccount.billing.cancelSubscription`) }}
                </a>
              </p>
            </div>
            <!-- -->

            <!-- WITH CANCEL AT PERIOD END -->
            <div v-else-if="userAccountStripeSubscription.cancelAtPeriodEnd">
              <!-- ACTIVE, INCOMPLETE AND PAST_DUE SUBSCRIPTION -->
              <div
                v-if="
                  ['active', 'incomplete', 'past_due'].indexOf(
                    userAccountStripeSubscription.status
                  ) > -1
                "
                class="mb-3"
              >
                {{ $t(`views.myAccount.billing.yourPlanWillBeCanceled`) }}
                {{ $t(`views.myAccount.billing.atDay`) }}
                {{
                  getDateFromTimestampUserFormat(userAccountStripeSubscription.currentPeriodEnd)
                }}.
                <br />
                {{ $t(`views.myAccount.billing.changeYourMind`) }}
              </div>
              <!-- TRIALING SUBSCRIPTION -->
              <div v-if="userAccountStripeSubscription.status === 'trialing'" class="mb-3">
                {{ $t(`views.myAccount.billing.trialEnd`) }}
                <span v-if="userAccountStripeSubscription.currentPeriodEnd">
                  {{ $t(`views.myAccount.billing.atDay`) }}
                  {{
                    getDateFromTimestampUserFormat(userAccountStripeSubscription.currentPeriodEnd)
                  }}.
                  <br />
                  {{ $t(`views.myAccount.billing.cancelTrialNotCharge`) }}
                </span>
              </div>

              <p class="mb-2">
                <a class="link-big" @click="renewSubscription">
                  {{ $t(`views.myAccount.billing.renewSubscription`) }}
                </a>
              </p>
            </div>
          </div>
        </template>
      </div>
    </div>

    <UpgradeModal :isModalActive.sync="isUpgradeModalActive" />
    <AccountCancellationModal :isModalActive.sync="isCancellationModalActive" />
  </v-card>
</template>

<script>
import { getAllowedPlans } from '@/lib/api.js'
import { getDateFromTimestampUserFormat } from '@/lib/utils/date.js'
import BillingMixin from '@/views/components/account/BillingMixin.js'
import BarLoading from '@/views/components/core/BarLoading'
import UpgradeModal from '@/views/components/common/upgrade/UpgradeModal'
import AccountCancellationModal from '@/views/components/account/AccountCancellationModal'
import AccountPastDueAlert from '@/views/components/account/AccountPastDueAlert'
import Currency from '@/views/components/common/Currency.vue'

export default {
  components: {
    BarLoading,
    UpgradeModal,
    AccountCancellationModal,
    AccountPastDueAlert,
    Currency,
  },
  mixins: [BillingMixin],
  data: () => ({
    loading: false,
    isUpgradeModalActive: false,
    isCancellationModalActive: false,
    limits: {},
  }),
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.xs
    },
  },
  async created() {
    const statusToPlan = {
      NO_PLAN: 'noPlan',
      PLAN_PREMIUM: 'premium',
      PLAN_PRO: 'pro',
      PLAN_UNLIMITED: 'unlimited',
      PLAN_CUSTOM: 'custom',
      TRIAL: 'pro',
    }
    try {
      const result = await getAllowedPlans()
      const allowedPlans = result.data.allowedPlans
      const limits = allowedPlans.find((item) => item.plan === statusToPlan[this.user.status])
      this.limits = {
        totalOperations: limits.totalOperations,
        maxTotalOperations: limits.maxTotalOperations,
        totalOperationsPercent: (limits.totalOperations / limits.maxTotalOperations) * 100,
        totalCostWithComissionBaseCurrency: limits.totalCostWithComissionBaseCurrency,
        maxTotalCostWithComissionBaseCurrency: limits.maxTotalCostWithComissionBaseCurrency,
        totalCostWithComissionBaseCurrencyPercent:
          (limits.totalCostWithComissionBaseCurrency /
            limits.maxTotalCostWithComissionBaseCurrency) *
          100,
      }
    } catch (error) {
      this.limits = {}
    }
  },
  methods: {
    getDateFromTimestampUserFormat,

    async upgradeToPremium() {
      this.isUpgradeModalActive = true
    },
    async changePaymentMethod() {
      this.goToCustomerPortal()
    },
    async updateSubscription() {
      this.goToCustomerPortal()
    },
    async completeIncompleteSubscription() {
      this.goToCustomerPortal()
    },
    async renewSubscription() {
      this.goToCustomerPortal()
    },
    cancelSubscription() {
      this.isCancellationModalActive = true
    },
    getPlanIcon() {
      if (this.user.status === 'PLAN_CUSTOM') return 'mdi-domain'
      else if (this.user.status === 'PLAN_PREMIUM') return 'mdi-rocket'
      else if (this.user.status === 'PLAN_PRO') return 'mdi-rocket-launch'
      else if (this.user.status === 'PLAN_UNLIMITED') return 'mdi-earth'
      else if (this.user.status === 'TRIAL') return 'mdi-rocket-launch'
    },
    getProgressLinearColor(percent) {
      if (percent < 70) return 'primary'
      else if (percent < 99) return 'accentYellow'
      else if (percent >= 99 && percent < Infinity) return 'softRed'
      else return 'primary'
    },
  },
}
</script>

<style lang="scss">
#account-profile-billing {
  .current-plan {
    font-weight: 300;
    font-size: 19px;
    &.small-font {
      font-size: 16px;
    }
  }
  .billing-period-end {
    font-size: 15px;
    font-weight: 300;
    a {
      font-size: 14px;
      font-weight: 400;
    }
  }
  .items-flex {
    display: flex;
    align-items: flex-start;
  }
  a.link-big {
    font-size: 15px;
  }

  .upgrade-button {
    text-transform: none;
    font-size: 14px;
  }

  .billing-limits {
    font-weight: 400;
    .billing-limits-progress-bar {
      max-width: 450px;
    }
  }
}
</style>
